/**
 * Print preview — the link between printDocument() (a plain function, used by
 * ~36 document screens) and the one <PrintPreviewHost /> mounted in App.js.
 *
 * printDocument builds the exact same print HTML as before and hands it here.
 * If the preview window is mounted it opens there (same tab, with Print and
 * Download PDF). If it is not mounted for any reason, the caller falls back to
 * the old behaviour — a new tab that prints straight away — so printing can
 * never stop working because of the preview.
 */

let listener = null;

/** Called once by <PrintPreviewHost />. Returns an unsubscribe function. */
export function registerPrintPreviewHost(fn) {
  listener = fn;
  return () => { if (listener === fn) listener = null; };
}

/**
 * Ask the preview window to open. Returns true when it did, false when no
 * preview window is mounted (the caller then prints the old way).
 */
export function openPrintPreview(payload) {
  if (typeof listener !== 'function' || !payload || !payload.html) return false;
  try {
    listener(payload);
    return true;
  } catch (e) {
    console.warn('[print preview] could not open, printing directly:', e && e.message);
    return false;
  }
}

/**
 * The original print path: open the HTML in a new tab and print it once its
 * images (logo, letterhead) have loaded — capped at 8 s so a slow or broken
 * image never blocks the print dialog. Returns false if the tab was blocked.
 */
export function printHtmlInNewWindow(html) {
  const w = window.open('', '_blank');
  if (!w) return false; // popup blocked
  w.document.write(html);
  w.document.close();

  const triggerPrint = () => {
    try { w.focus(); } catch (e) { /* ignore */ }
    try { w.print(); } catch (e) { console.error('Print failed:', e); }
  };

  const images = Array.from(w.document.images || []);
  if (images.length === 0) {
    triggerPrint();
    return true;
  }

  const imagePromises = images.map(img => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise(resolve => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  });

  const timeoutPromise = new Promise(resolve => setTimeout(resolve, 8000));
  Promise.race([Promise.all(imagePromises), timeoutPromise]).then(triggerPrint);
  return true;
}
